import { PlusOutlined, UpOutlined } from "@ant-design/icons";
import {
  Form,
  Input,
  Button,
  type FormProps,
  Flex,
  theme,
  Dropdown,
  MenuProps,
  Select,
  Spin,
  Modal,
} from "antd";
import { useForm } from "antd/es/form/Form";
import { API_URL_CONTACTS, API_URL_QUOTES } from "@/services/XCONS_ENV";
import { axiosInstance, identityHydrationNotices } from "@/utilities";
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PhoneNumberInput } from "@/components/phone-number-input";
import { useSelectedStores } from "@/config/context/SelectedItemsContext";
import { notification } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { message } from "antd";
import {
  useContactConflictCheck,
  type ContactLookupMatch,
} from "@/hooks/useContactConflictCheck";

export const ContactsCreate: React.FC<{
  formProps: FormProps;
  t?: any;
  closeModal?: () => void;
  storesSelectProps?: any;
  refetch?: () => void;
  /**
   * Se llama con el contacto recién creado, si el que abre el panel necesita hacer algo con él.
   *
   * El listado no la usa —ahí alcanza con `refetch`—, pero desde el dashboard hay que ir a la ficha
   * del contacto nuevo (QA #273.9), y para eso hace falta su id.
   */
  onCreated?: (contact: any) => void;
}> = ({ formProps, t, closeModal, storesSelectProps, refetch, onCreated }) => {
  const { selectedStores } = useSelectedStores();
  const [storeCode, setStoreCode] = useState<string>(
    selectedStores[0]?.code || "",
  );
  const [createLoading, setCreateLoading] = useState<boolean>(false);

  const [form] = useForm();
  const { token } = theme.useToken();
  const navigate = useNavigate();
  // When the operator picks "+ New Quote" from the split button, we create the
  // contact and then an empty quote in the same store, navigating to it.
  const createQuoteAfterRef = useRef(false);

  const {
    runConflictCheck,
    applyConflictErrors,
    consumeBackendConflict,
  } = useContactConflictCheck(form);

  const handleConflictCheck = async () => {
    const whatsapp = form.getFieldValue("whatsapp");
    const email = form.getFieldValue("email");
    const code = form.getFieldValue("storeCode") || storeCode;
    const { whatsappError, emailError } = await runConflictCheck(
      whatsapp,
      email,
      code,
    );
    applyConflictErrors(whatsappError, emailError);
  };

  const fieldLabel = (field: "email" | "whatsapp") =>
    field === "email"
      ? t("contacts.identity.field.email", { defaultValue: "e-mail" })
      : t("contacts.identity.field.whatsapp", { defaultValue: "número de WhatsApp" });

  /**
   * La ficha existe en este store pero el operador no puede verla (xcons-docs#549): con Contactos en
   * "Por participación" y sin participar de esa ficha, o en "No". No se ofrece abrirla —abría vacía—
   * ni se dice cómo se llama la persona: se avisa que ya es contacto del store y a quién pedirle.
   * El operador **sí** tiene permiso sobre Contactos; lo que le falta es participar de la ficha, y
   * por eso el texto no dice "no tenés permisos".
   */
  const explainContactOutOfReach = (field: "email" | "whatsapp") => {
    Modal.info({
      title: t(
        "contacts.create.duplicate.noAccess.title",
        "Esa persona ya es contacto de este store",
      ),
      content: t("contacts.create.duplicate.noAccess.body", {
        field: fieldLabel(field),
        defaultValue:
          "Hay una ficha con ese {{field}} en este store, pero no participás de ella: no la podés abrir ni crear otra. Pedile a quien administra los contactos del store que te agregue como participante.",
      }),
      okText: t("contacts.create.duplicate.noAccess.ok", "Entendido"),
    });
  };

  /**
   * Ya existe una ficha de esa persona en ESTE store: no hay nada que crear. En vez de dejar un error
   * pegado al campo, se ofrece ir a la ficha — que es lo que el operador quería hacer. Si el
   * operador no puede ver esa ficha, se le explica en vez de mandarlo a una página vacía.
   */
  const offerExistingContact = (match: ContactLookupMatch, field: "email" | "whatsapp") => {
    if (match.canViewContact === false || !match.contactId) {
      explainContactOutOfReach(field);
      return;
    }
    const contactId = match.contactId;
    Modal.confirm({
      title: t(
        "contacts.create.duplicate.title",
        "Ese contacto ya existe en este store",
      ),
      content: t("contacts.create.duplicate.body", {
        field: fieldLabel(field),
        defaultValue:
          "Ese {{field}} ya es de un contacto de este store. No hace falta crearlo de nuevo: podés abrir su ficha.",
      }),
      okText: t("contacts.create.duplicate.ok", "Abrir la ficha"),
      cancelText: t("contacts.create.duplicate.cancel", "Corregir el dato"),
      onOk: () => {
        closeModal?.();
        navigate(`/contacts/${contactId}`);
      },
    });
  };

  /**
   * El e-mail es de un contacto y el teléfono de otro, y los dos tienen ficha en este store: eso no
   * se puede crear ni reusar, pero **son dos fichas de la misma persona**. Se ofrece unificarlas.
   *
   * Se navega a la ficha del contacto del e-mail con `?merge=<el otro>`: la ficha abre el drawer de
   * unificación con el origen ya elegido. El destino por default es el del e-mail (un e-mail real es
   * la identidad más fuerte); si había que conservar el otro, en el drawer se cambia con **Volver**.
   */
  const offerMergeOfTwoContacts = (
    targetContactId?: number | null,
    sourceContactId?: number | null,
  ) => {
    if (!targetContactId || !sourceContactId) return false;

    Modal.confirm({
      title: t("contacts.create.twoIdentities.title", "¿Son la misma persona?"),
      width: 520,
      content: t(
        "contacts.create.twoIdentities.body",
        "El e-mail y el teléfono que cargaste son de dos contactos distintos de este store. Si son la misma persona, se unifican en uno: el que se queda absorbe los presupuestos, pedidos, direcciones e historial del otro.",
      ),
      okText: t("contacts.create.twoIdentities.ok", "Unificar contactos"),
      cancelText: t("contacts.create.twoIdentities.cancel", "Corregir los datos"),
      onOk: () => {
        closeModal?.();
        navigate(`/contacts/${targetContactId}?merge=${sourceContactId}`);
      },
    });
    return true;
  };

  const onSubmit = async (values: any) => {
    // Re-check on submit in case the operator typed and submitted before the
    // on-blur lookup had a chance to fire (e.g., Enter on the email field).
    const finalStoreCode = values.storeCode || storeCode;
    const { whatsappError, emailError, byWhatsapp, byEmail } =
      await runConflictCheck(values.whatsapp, values.email, finalStoreCode);
    // Only a same-store duplicate / identity mismatch blocks the submit.
    if (whatsappError || emailError) {
      applyConflictErrors(whatsappError, emailError);

      // Primero el caso de DOS identidades: el e-mail es de un contacto de este store y el teléfono
      // de otro. Va antes que el duplicado simple porque ahí los dos campos dan "ya existe", y
      // ofrecer "abrir la ficha" de uno de los dos dejaría el problema real sin resolver.
      const twoIdentities =
        byEmail &&
        byWhatsapp &&
        byEmail.userId !== byWhatsapp.userId &&
        byEmail.isContactInStore &&
        byWhatsapp.isContactInStore;
      if (twoIdentities) {
        // Unificar es ver las dos fichas: si alguna está fuera del alcance, se explica esa y
        // listo (xcons-docs#549). El caso real lo resuelve quien sí las ve.
        if (byEmail.canViewContact === false) {
          explainContactOutOfReach("email");
          return;
        }
        if (byWhatsapp.canViewContact === false) {
          explainContactOutOfReach("whatsapp");
          return;
        }
        if (offerMergeOfTwoContacts(byEmail.contactId, byWhatsapp.contactId)) return;
      }

      // Duplicado en ESTE store: la ficha ya existe, se ofrece abrirla (o se explica por qué no).
      const existing = byEmail?.isContactInStore
        ? { match: byEmail, field: "email" as const }
        : byWhatsapp?.isContactInStore
          ? { match: byWhatsapp, field: "whatsapp" as const }
          : null;
      if (existing) offerExistingContact(existing.match, existing.field);
      return;
    }
    // Que la identidad ya exista en OTRO store no se avisa: el alta procede igual (una fila de
    // contacto por store) y el aviso sólo agregaba un cartel a un camino que era correcto.
    setCreateLoading(true);

    try {
      /**
       * OpenSpec 006 · D1 — el alta es UNA llamada.
       *
       * Antes esto era `POST /users` y después `POST /contacts`, y el formulario sostenía la
       * invariante "una identidad por persona, una ficha por store". Con dos llamadas, que la
       * primera saliera bien y la segunda fallara dejaba una **identidad huérfana**: una cuenta
       * que existe, ocupa el mail y nadie ve en el admin. Ahora el peor caso es un alta que el
       * endpoint rechaza.
       */
      const requestData = {
        storeCode: finalStoreCode,
        // Datos de identidad: el backend resuelve si la reusa o la crea.
        email: values.email,
        whatsapp: values.whatsapp,
        description: "",
        isActive: true,
        // OpenSpec 024 — el nombre tipeado va a la FICHA de este store.
        //
        // Antes se perdía cuando el alta reusaba una identidad existente: `POST /users` sólo lo usa
        // en el INSERT de una cuenta nueva, y escribirlo sobre una cuenta que ya existía habría
        // renombrado a la persona en todos los stores donde tiene ficha. Ahora el nombre es del
        // store, así que ya no hay razón para descartarlo.
        firstname: values.firstname,
        lastname: values.lastname,
      };

      const { data: createdContact } = await axiosInstance.post(
        `${API_URL_CONTACTS}`,
        requestData,
      );
      // D4: reusar una identidad no es un caso de borde, es el frecuente. Decirlo importa —
      // si no, el operador ve después un contacto con datos que él no cargó y no sabe por qué.
      if (createdContact?.identityCreated === false) {
        message.success(t("contacts.message.success.newcontactcreated"));
      } else {
        message.success(t("contacts.message.success.contactcreated"));
      }

      /**
       * D8 — y qué dato, puntualmente, salió del ecosistema en vez de lo que se tipeó. El mensaje
       * de arriba dice "se usaron sus datos existentes"; este dice **cuáles**, que es lo que el
       * operador necesita para no volver a corregirlo a mano.
       */
      const notices = identityHydrationNotices(createdContact, t);
      if (notices.hydrated) message.success(notices.hydrated);
      if (notices.discarded) message.warning(notices.discarded);

      // "+ New Quote": create an empty quote in the same store and go to it.
      if (createQuoteAfterRef.current) {
        createQuoteAfterRef.current = false;
        const storeObj = (storesSelectProps ?? []).find(
          (s: any) => s?.code === finalStoreCode,
        );
        const storeId = storeObj?.store_id ?? storeObj?.storeId;
        if (storeId) {
          const { data } = await axiosInstance.post(API_URL_QUOTES, { storeId });
          const newQuoteId = data?.quoteId;
          if (newQuoteId) {
            closeModal?.();
            navigate(`/quotes/${newQuoteId}`);
            return;
          }
        }
        message.error(
          "Contacto creado, pero no se pudo crear el presupuesto",
        );
      }

      refetch?.();
      if (onCreated) {
        closeModal?.();
        onCreated(createdContact);
      }
    } catch (error: any) {
      // Don't leave the flag stuck if the contact creation itself failed.
      createQuoteAfterRef.current = false;
      // Backend returns BadRequestException with {code, field, message} for
      // identity conflicts that slipped past the on-blur lookup (race or the
      // operator bypassed the blur). Pin to the right Form.Item.
      const payload = error?.response?.data;
      const inStore = (side: any) =>
        side?.contacts?.find((c: any) => c.storeCode === finalStoreCode)?.contactId;

      /**
       * OpenSpec 006 · D3 — los tres casos bloqueados vienen del backend con un `code` y los
       * datos para construir la salida. Antes esos datos los averiguaba el front por su cuenta;
       * ahora llegan en la respuesta del error, que es la única que vio el estado real al
       * momento de escribir.
       */
      if (payload?.code === "TWO_CONTACTS_SAME_PERSON") {
        if (
          offerMergeOfTwoContacts(
            payload.targetContactId,
            payload.sourceContactId,
          )
        ) {
          return;
        }
      }
      if (payload?.code === "CONTACT_ALREADY_IN_STORE" && payload?.contactId) {
        offerExistingContact(
          payload.contactId,
          payload.field === "email" ? "email" : "whatsapp",
        );
        return;
      }
      if (payload?.code === "PHONE_BELONGS_TO_OTHER_IDENTITY") {
        applyConflictErrors(
          payload.message ??
            t(
              "contacts.create.phoneOtherIdentity",
              "Ese teléfono es de otra persona",
            ),
          null,
        );
        return;
      }

      if (
        payload?.code === "CONFLICT_WHATSAPP_AND_EMAIL_DIFFERENT_USERS" &&
        (payload?.sharedStores ?? []).includes(finalStoreCode) &&
        offerMergeOfTwoContacts(inStore(payload.byEmail), inStore(payload.byWhatsapp))
      ) {
        consumeBackendConflict(error);
      } else if (consumeBackendConflict(error)) {
        // already pinned
      } else if (
        error?.response?.data?.message ===
        "An user with that whatsapp or email already exists"
      ) {
        notification.warning({
          message: <strong>{t("contacts.message.operatorregistered")}</strong>,
          description: t("contacts.message.operatorregistereddescription"),
          duration: 4.5,
          placement: "top",
          icon: <ExclamationCircleOutlined style={{ color: "#FAAD14" }} />,
        });
      } else {
        console.error("Error creating user", error);
      }
    } finally {
      setCreateLoading(false);
    }
  };

  const onMenuClick: MenuProps["onClick"] = (e) => {
    if (e.key === "new-quote") {
      // Create the contact first (same flow), then create the quote in onSubmit.
      createQuoteAfterRef.current = true;
      form.submit();
    }
  };

  const items = [
    {
      key: "new-quote",
      label: "+ New Quote",
    },
  ];

  return (
    <Form layout="vertical" {...formProps} form={form} onFinish={onSubmit} style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>
      <div style={{ flex: 1, overflowY: "auto", padding: "16px 24px" }}>
      <Flex gap={8} justify="space-between">
        <Form.Item
          style={{ flex: 1 }}
          label={
            <span style={{ color: token.colorTextTertiary }}>{t("name")}</span>
          }
          name="firstname"
          hasFeedback
          rules={[
            {
              required: true,
              whitespace: true,
              message: t("message.error.inputEmpty"),
            },
          ]}
        >
          <Input disabled={createLoading} type="text" />
        </Form.Item>

        <Form.Item
          style={{ flex: 1 }}
          label={
            <span style={{ color: token.colorTextTertiary }}>
              {t("lastname")}
            </span>
          }
          name="lastname"
          hasFeedback
          rules={[
            {
              required: true,
              message: t("message.error.inputEmpty"),
            },
          ]}
        >
          <Input disabled={createLoading} type="text" />
        </Form.Item>
      </Flex>

      <Form.Item
        label={<span style={{ color: token.colorTextTertiary }}>WhatsApp</span>}
        name="whatsapp"
        dependencies={["email"]}
        hasFeedback
        rules={[
          // 1) Regla "uno de los dos"
          ({ getFieldValue }) => ({
            validator: async (_, value) => {
              const email = getFieldValue("email");
              const hasEmail = !!String(email ?? "").trim();
              const phoneDigits = String(value ?? "").replace(/\D/g, "");
              const hasWhatsapp = phoneDigits.length > 0;

              if (!hasEmail && !hasWhatsapp) {
                return Promise.reject(
                  new Error(t("contacts.address.validation.emailOrWhatsappRequired") ?? "Ingresá email o WhatsApp"),
                );
              }

              return Promise.resolve();
            },
          }),

          // 2) Validación de largo SOLO si escribió whatsapp
          {
            validator: async (_, value) => {
              const phoneDigits = String(value ?? "").replace(/\D/g, "");
              if (!phoneDigits) return Promise.resolve();
              if (phoneDigits.length < 11) {
                return Promise.reject(
                  new Error(t("contacts.address.validation.whatsapplength")),
                );
              }
              return Promise.resolve();
            },
          },
        ]}
      >
        <PhoneNumberInput
          disabled={createLoading}
          inputProps={{ onBlur: handleConflictCheck }}
        />
      </Form.Item>

      <Form.Item
        label={
          <span style={{ color: token.colorTextTertiary }}>
            {t("pages.login.fields.email")}
          </span>
        }
        name="email"
        dependencies={["whatsapp"]}
        hasFeedback
        rules={[
          // 1) Regla "uno de los dos"
          ({ getFieldValue }) => ({
            validator: async (_, value) => {
              const whatsapp = getFieldValue("whatsapp");
              const phoneDigits = String(whatsapp ?? "").replace(/\D/g, "");
              const hasWhatsapp = phoneDigits.length > 0;

              const email = String(value ?? "").trim();
              const hasEmail = email.length > 0;

              if (!hasWhatsapp && !hasEmail) {
                return Promise.reject(
                  new Error(t("contacts.address.validation.emailOrWhatsappRequired") ?? "Ingresá email o WhatsApp"),
                );
              }

              return Promise.resolve();
            },
          }),

          // 2) Validación de formato SOLO si escribió email
          {
            validator: async (_, value) => {
              const email = String(value ?? "").trim();
              if (!email) return Promise.resolve();
              const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
              if (!valid) {
                return Promise.reject(new Error(t("pages.register.errors.validEmail")));
              }
              return Promise.resolve();
            },
          },
        ]}
      >
        <Input
          disabled={createLoading}
          type="email"
          onBlur={handleConflictCheck}
        />
      </Form.Item>

      <Form.Item
        label={<span style={{ color: token.colorTextTertiary }}>Store</span>}
        name="storeCode"
        initialValue={selectedStores[0]?.code}
        rules={[
          {
            required: true,
            message: t("contacts.validation.store.required"),
          },
        ]}
      >
        <Flex vertical>
          <Select
            disabled={createLoading}
            allowClear
            options={storesSelectProps?.map((option: any) => ({
              label: option?.name as string,
              value: option?.code as string,
            }))}
            defaultValue={selectedStores[0]?.code}
            onChange={(value) => {
              setStoreCode(value);
              form.setFields([{ name: "storeCode", value, errors: [] }]);
            }}
          />
        </Flex>
      </Form.Item>

      </div>
      <Flex
        align="center"
        justify="end"
        gap="8px"
        style={{ flexShrink: 0, padding: "12px 24px", borderTop: "1px solid #f0f0f0", backgroundColor: "#fff" }}
      >
        <Button type="default" danger onClick={closeModal}>
          {t("buttons.cancel")}
        </Button>
        <Dropdown.Button
          style={{ width: "auto" }}
          htmlType="submit"
          type="primary"
          icon={<UpOutlined />}
          menu={{ items, onClick: onMenuClick }}
          placement="topRight"
          /* El chequeo de conflictos NO bloquea este botón: el `blur` del campo dispara antes que
             el `click`, así que el botón quedaba deshabilitado entre el mousedown y el mouseup y
             **se perdía el primer click** — había que apretar dos veces para crear. El submit
             vuelve a correr el chequeo por su cuenta, así que bloquear acá no agregaba nada. */
          loading={createLoading}
        >
          {t("button.createnewcontact")}
        </Dropdown.Button>
      </Flex>
    </Form>
  );
};
